import {
  ComponentType,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useState,
  forwardRef,
  useMemo,
  useEffect,
} from "react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size as floatingSize,
  hide,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  Placement,
  FloatingPortal,
  FloatingFocusManager,
} from "@floating-ui/react";
import { Icon, IconName } from "../Icon";
import cx from "classnames";
import "./Combobox.scss";
import { Input } from "../Input";
import { Chip } from "../Chip/Chip";
import { Checkbox } from "../Checkbox";
import { Flex } from "../Flex";

type ComboboxItem = {
  label: ReactNode;
  searchLabel?: string;
  value: string;
  disabled?: boolean;
  href?: string;
};

type ComboboxItems = ComboboxItem[];

enum ComboboxSelectionType {
  SimpleValue = "simpleValue",
  MultiSelect = "selectionType",
}

type ComboboxSelectionValue =
  | {
      type: ComboboxSelectionType.SimpleValue;
      value: string;
    }
  | {
      type: ComboboxSelectionType.MultiSelect;
      value: string[];
    };

/**
 * Combobox orientation options
 */
type ComboboxOrientation = "down" | "up";

type ComboboxProps = {
  options: ComboboxItems;
  selected?: string | string[] | null;
  placeholder?: string;
  onChange(value: ComboboxSelectionValue): void;
  label?: string;
  iconName?: IconName | ReactElement | ComponentType<any>;
  iconProps?: Record<string, any>;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  size?: "md" | "lg";
  variant?: "default" | "translucent";
  orientation?: ComboboxOrientation;
  offsetX?: number;
  offsetY?: number;
  customTrigger?: ReactNode;
  required?: boolean;
  hasSearch?: boolean;
  selectionType?: "single" | "multiple";
  openChanged?: (open: boolean) => void;
  usePortal?: boolean;
  dropdownWidth?: React.CSSProperties["width"];
  menuItemClasses?: string;
  menuClasses?: string;
  triggerClasses?: string;
  triggerLabelClasses?: string;
  renderSelectedItem?: (option: ComboboxItem) => ReactNode;
  indeterminateValues?: string[];
};

/**
 * A combobox component that allows users to select from a list of options and search via a search input.
 */
const Combobox = forwardRef<HTMLDivElement, ComboboxProps>(
  (
    {
      options,
      selected,
      onChange,
      placeholder,
      label,
      iconName,
      iconProps = {},
      disabled = false,
      error = false,
      className,
      size = "md",
      variant = "default",
      orientation = "down",
      offsetX = 0,
      offsetY = 4,
      customTrigger,
      openChanged,
      required = false,
      hasSearch = true,
      selectionType = "single",
      usePortal = true,
      dropdownWidth,
      menuItemClasses,
      menuClasses,
      triggerClasses: triggerClassesProp,
      triggerLabelClasses: triggerLabelClassesProp,
      renderSelectedItem,
      indeterminateValues,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);

    // Internal state for multi-select values
    const [selectedValues, setSelectedValues] = useState<string[]>(() => {
      if (selectionType === "multiple" && Array.isArray(selected)) {
        return selected;
      }
      if (selectionType === "single" && typeof selected === "string") {
        return [selected];
      }
      return [];
    });

    // Keep internal selectedValues in sync when the `selected` prop changes.
    // The Combobox stores selection internally for snappy UI, but when the
    // parent drives `selected` (for example from URL params) we need to
    // reflect that change immediately.
    useEffect(() => {
      if (selectionType === "multiple") {
        setSelectedValues(Array.isArray(selected) ? selected : []);
      } else {
        setSelectedValues(
          typeof selected === "string" && selected ? [selected] : [],
        );
      }
    }, [selected, selectionType]);

    const placement: Placement =
      orientation === "down" ? "bottom-start" : "top-start";

    const onOpenChange = useCallback(
      (open: boolean) => {
        if (disabled) return;
        if (openChanged) openChanged(open);
        setIsOpen(open);
        // Clear search when closing and reset focused index
        if (!open) {
          setSearchValue("");
          setFocusedIndex(-1);
        }
      },
      [disabled, openChanged],
    );

    const { context, refs, floatingStyles } = useFloating({
      open: isOpen,
      onOpenChange,
      placement: placement,
      middleware: [
        offset({
          mainAxis: offsetY,
          crossAxis: offsetX,
        }),
        flip(),
        shift(),
        floatingSize({
          apply({ rects, elements }) {
            Object.assign(elements.floating.style, {
              width: dropdownWidth
                ? dropdownWidth
                : `${rects.reference.width}px`,
              maxHeight: "300px",
            });
          },
        }),
        hide(),
      ],
      whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context, {
      ancestorScroll: true,
      outsidePress: true,
      escapeKey: true,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      click,
      dismiss,
    ]);

    // Filter options based on search value
    const filteredOptions = useMemo(() => {
      if (!searchValue.trim()) {
        return options;
      }

      const searchLower = searchValue.toLowerCase();

      return options.filter((option) => {
        const labelSearchable =
          typeof option.label === "string"
            ? option.label.toLowerCase()
            : option.searchLabel?.toLowerCase() || "";

        return (
          labelSearchable.includes(searchLower) ||
          option.value.toLowerCase().includes(searchLower) ||
          option.searchLabel?.toLowerCase().includes(searchLower)
        );
      });
    }, [options, searchValue]);

    const getSelectedOptionLabel = useCallback(() => {
      if (selectionType === "multiple") {
        if (selectedValues.length === 0) {
          return placeholder || "No selection";
        }
        const selectedOptions = selectedValues
          .map((v) => options.find((o) => o.value === v))
          .filter((o): o is ComboboxItem => Boolean(o));

        if (renderSelectedItem) {
          return selectedOptions.map((option, idx) => (
            <span key={option.value}>
              {idx > 0 ? ", " : null}
              {renderSelectedItem(option)}
            </span>
          ));
        }

        if (selectedValues.length === 1) {
          const selectedOption = selectedOptions[0];
          return selectedOption ? (
            <Chip label={selectedOption.label as string} />
          ) : (
            <Chip label={selectedValues[0]} />
          );
        }
        return selectedOptions.map((value) => {
          return <Chip key={value.value} label={value.label as string} />;
        });
      } else if (selectionType === "single") {
        if (selectedValues.length > 0) {
          const selectedOption = options.find(
            (option) => option.value === selectedValues[0],
          );
          if (selectedOption) {
            return renderSelectedItem
              ? renderSelectedItem(selectedOption)
              : selectedOption.label;
          }
        }
        return placeholder || "No selection";
      }
    }, [
      options,
      selectedValues,
      placeholder,
      selectionType,
      renderSelectedItem,
    ]);

    const selectedOptionLabel = getSelectedOptionLabel();

    const onClickMenuItem = useCallback(
      (item: ComboboxItem) => {
        if (disabled || item.disabled) return;

        const value = item.value;

        if (selectionType === "multiple") {
          const newValues = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];

          setSelectedValues(newValues);
          onChange({
            type: ComboboxSelectionType.MultiSelect,
            value: newValues,
          });
        } else {
          if (selectedValues.includes(value)) {
            setSelectedValues([]);
            onChange({
              type: ComboboxSelectionType.SimpleValue,
              value: "",
            });
          } else {
            setSelectedValues([value]);
            onChange({
              type: ComboboxSelectionType.SimpleValue,
              value,
            });
          }
          setIsOpen(false);
        }
      },
      [onChange, disabled, selectionType, selectedValues],
    );

    const onSearchChange = useCallback((value: string) => {
      setSearchValue(value);
      setFocusedIndex(-1);
    }, []);

    const handleKeyboardNavigation = useCallback(
      (e: React.KeyboardEvent) => {
        if (!isOpen || filteredOptions.length === 0) return;

        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            setFocusedIndex((prev) => {
              const nextIndex = prev + 1;
              return nextIndex >= filteredOptions.length ? 0 : nextIndex;
            });
            break;
          case "ArrowUp":
            e.preventDefault();
            setFocusedIndex((prev) => {
              const nextIndex = prev - 1;
              return nextIndex < 0 ? filteredOptions.length - 1 : nextIndex;
            });
            break;
          case "Enter":
            e.preventDefault();
            if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
              onClickMenuItem(filteredOptions[focusedIndex]);
            }
            break;
          case "Escape":
            e.preventDefault();
            setIsOpen(false);
            break;
        }
      },
      [isOpen, filteredOptions, focusedIndex, onClickMenuItem],
    );

    const renderIcon = () => {
      if (!iconName) return null;
      if (typeof iconName === "string") {
        return (
          <Icon iconName={iconName as IconName} {...iconProps} />
        );
      }
      if (isValidElement(iconName)) {
        return iconName;
      }
      const IconComponent = iconName as ComponentType<any>;
      return <IconComponent {...iconProps} />;
    };

    const comboboxClasses = cx(
      "Combobox",
      {
        "Combobox--Disabled": disabled,
        "Combobox--Error": error,
        "Combobox--Large": size === "lg",
        "Combobox--Up": orientation === "up",
        "Combobox--Translucent": variant === "translucent",
      },
      className,
    );

    const triggerClasses = cx(
      "Combobox__Trigger",
      {
        "Combobox__Trigger--IsOpen": isOpen,
        "Combobox__Trigger--Error": error,
        "Combobox__Trigger--Disabled": disabled,
      },
      triggerClassesProp,
    );

    const triggerLabelClasses = cx(
      "Combobox__Trigger__Label",
      {
        "Combobox__Trigger__Label--Filled": selectedValues.length > 0,
        "Combobox__Trigger__Label--Custom": Boolean(renderSelectedItem),
      },
      triggerLabelClassesProp,
    );

    const renderLabelContent = (option: ComboboxItem) => {
      return option.label;
    };

    const renderComboboxMenu = () => (
      <div
        className={cx("Combobox__Menu", menuClasses)}
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        onKeyDown={handleKeyboardNavigation}
      >
        {hasSearch && (
          <>
            <div className={"Combobox__SearchLabel"}>Search</div>
            <Input
              name="ComboboxSearch"
              value={searchValue}
              onChange={(_, val) => onSearchChange(val)}
              prefix={<Icon iconName="Search" size={16} />}
              placeholder="Search..."
              suffix={
                searchValue &&
                searchValue.length > 0 && (
                  <Icon
                    onClick={() => onSearchChange("")}
                    iconName="X"
                    size={16}
                  />
                )
              }
              autoFocus
            />
          </>
        )}
        <div className="Combobox__Menu__ItemContainer">
          {filteredOptions.length === 0 ? (
            <div className="Combobox__Menu__NoResults">No results found</div>
          ) : (
            filteredOptions.map((option, index) => {
              const itemClasses = cx("Combobox__Menu__Item", menuItemClasses, {
                "Combobox__Menu__Item--Selected": selectedValues.includes(
                  option.value,
                ),
                "Combobox__Menu__Item--Disabled": option.disabled,
                "Combobox__Menu__Item--Focused": index === focusedIndex,
              });
              const handleClick = (e: React.MouseEvent) => {
                e.preventDefault();
                onClickMenuItem(option);
              };
              const refCallback = (el: HTMLElement | null) => {
                if (index === focusedIndex && el) {
                  el.scrollIntoView({ block: "nearest" });
                }
              };
              const itemContent = (
                <>
                  {selectionType === "multiple" && (
                    <Checkbox
                      as="component"
                      checked={selectedValues.includes(option.value)}
                      indeterminate={
                        !selectedValues.includes(option.value) &&
                        (indeterminateValues?.includes(option.value) ?? false)
                      }
                      onChange={(event) => {
                        event.stopPropagation();
                      }}
                      disabled={option.disabled}
                    />
                  )}
                  <div className="Combobox__Menu__Item__Content">
                    {renderLabelContent(option)}
                  </div>
                </>
              );

              return option.href ? (
                <a
                  className={itemClasses}
                  key={option.value}
                  href={option.href}
                  onClick={handleClick}
                  ref={refCallback}
                >
                  {itemContent}
                </a>
              ) : (
                <div
                  className={itemClasses}
                  key={option.value}
                  onClick={handleClick}
                  ref={refCallback}
                >
                  {itemContent}
                </div>
              );
            })
          )}
        </div>
      </div>
    );

    return (
      <div className={comboboxClasses} ref={ref}>
        {label && (
          <div
            className={cx("Combobox__Label", {
              "Combobox__Label--Error": error,
            })}
          >
            {label}
            {required && <span className="Combobox__Required">*</span>}
          </div>
        )}
        {customTrigger ? (
          <div ref={refs.setReference} {...getReferenceProps()}>
            {customTrigger}
          </div>
        ) : (
          <div
            className={triggerClasses}
            ref={refs.setReference}
            {...getReferenceProps()}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (!isOpen && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                setIsOpen(true);
              } else if (isOpen) {
                handleKeyboardNavigation(e);
              }
            }}
          >
            <Flex direction="row" align="center" gap="1">
              {iconName && (
                <div className="Combobox__Trigger__Prefix">{renderIcon()}</div>
              )}
              <div className={triggerLabelClasses}>
                {selectionType === "multiple" && selectedValues.length > 1
                  ? selectedOptionLabel
                  : selectedOptionLabel}
              </div>
            </Flex>
            {isOpen ? (
              <div className="Combobox__Trigger__Suffix">
                <Icon
                  iconName="ChevronUp"
                  className="Combobox__Trigger__Icon"
                />
              </div>
            ) : (
              <div className="Combobox__Trigger__Suffix">
                <Icon
                  iconName="ChevronDown"
                  className="Combobox__Trigger__Icon"
                />
              </div>
            )}
          </div>
        )}

        {isOpen &&
          !disabled &&
          (usePortal ? (
            <FloatingPortal>
              <FloatingFocusManager
                context={context}
                modal={false}
                initialFocus={-1}
              >
                {renderComboboxMenu()}
              </FloatingFocusManager>
            </FloatingPortal>
          ) : (
            renderComboboxMenu()
          ))}
      </div>
    );
  },
);

export { Combobox, ComboboxSelectionType };
export type { ComboboxItem, ComboboxItems, ComboboxSelectionValue };
